import { useGlobalSearchParams } from "expo-router";
import { useContext, useEffect, useRef } from "react";

import { ACCESS_TRACKS } from "@/access/tracks";
import {
  useGetClinicRequest,
  useGetOrCreateUserProgression,
  useGetPreClinicSteps,
} from "@/api/generated";
import { ProgramGroup } from "@/api/generated/models";
import { LayoutContext } from "@/components/LayoutProvider";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { captureException } from "@/sentry";
import {
  isPreClinicFlowResolved,
  useHasPreClinicStepsStoreHydrated,
  usePreClinicStepsStore,
} from "@/store";

/**
 * Drives a program conversation created from an entry link: collect the pre-clinic steps, then
 * open enrollment.
 */
export default function ProgramEnrollmentGate() {
  const conversationId = useCurrentConversationId() ?? "";
  const { clinic_request_id: clinicRequestId = "" } = useGlobalSearchParams<{
    clinic_request_id?: string;
  }>();
  const hasPreClinicStepsHydrated = useHasPreClinicStepsStoreHydrated();
  const startPreClinic = usePreClinicStepsStore((s) => s.startPreClinic);
  const { setModalContent } = useContext(LayoutContext);
  const router = usePropagatedRouter();
  const { mutateAsync: getOrCreateUserProgression } =
    useGetOrCreateUserProgression();
  const enrollmentAttemptKey = useRef<string | null>(null);

  const { data: clinicRequest } = useGetClinicRequest(
    conversationId,
    clinicRequestId,
    { query: { enabled: Boolean(conversationId && clinicRequestId) } },
  );
  const program = clinicRequest?.program;
  const enrollmentFlow =
    program?.group === ProgramGroup.access
      ? ACCESS_TRACKS[program.type].flowName
      : undefined;
  const isProgramConversation = Boolean(
    conversationId && clinicRequestId && enrollmentFlow,
  );

  const { data, isLoading } = useGetPreClinicSteps(
    { clinic_request_id: clinicRequestId },
    { query: { enabled: isProgramConversation } },
  );
  const progress = usePreClinicStepsStore((s) =>
    clinicRequestId ? s.progressByClinicRequestId[clinicRequestId] : undefined,
  );

  useEffect(() => {
    if (!conversationId || !clinicRequestId || !enrollmentFlow) {
      enrollmentAttemptKey.current = null;
      return;
    }
    if (!hasPreClinicStepsHydrated) return;
    if (isLoading || !data) return;

    if (!isPreClinicFlowResolved(data.steps, progress)) {
      startPreClinic(conversationId, clinicRequestId, [...data.steps]);
      return;
    }

    const requestKey = `${conversationId}:${clinicRequestId}`;
    if (enrollmentAttemptKey.current === requestKey) return;
    enrollmentAttemptKey.current = requestKey;
    getOrCreateUserProgression({
      data: {
        flow_name: enrollmentFlow,
        clinic_request_id: clinicRequestId,
        conversation_id: conversationId,
      },
    })
      .then((progression) => {
        setModalContent(() => () => (
          <UserProgressionFlow
            initialProgression={progression}
            onClose={() => setModalContent(null)}
          />
        ));
        // The gate has everything it needs in the modal's closure now. Dropping the request id
        // stops a remount (reload, back) from building a second enrollment progression.
        router.replace(`/conversation/${conversationId}`);
      })
      .catch((error) => {
        captureException(error, {
          extra: { clinic_request_id: clinicRequestId, program },
        });
        router.replace(`/conversation/${conversationId}`);
      });
  }, [
    hasPreClinicStepsHydrated,
    isLoading,
    data,
    progress,
    conversationId,
    clinicRequestId,
    program,
    enrollmentFlow,
    startPreClinic,
    getOrCreateUserProgression,
    setModalContent,
    router,
  ]);

  return null;
}
